import React, { useEffect, useState } from "react";
import { Phone, BookOpen, ListChecks, KeyRound, Sparkles, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Dashboard card explaining how to call our Twilio number from a detention center.
 * Auto-hides if the number isn't yet provisioned (returns a "coming soon" state instead).
 */
export default function CallFromDetentionCard() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/system/call-info").then((r) => setInfo(r.data)).catch(() => setInfo({}));
  }, []);

  const copyNumber = () => {
    if (!info?.phone_number) return;
    navigator.clipboard.writeText(info.phone_number);
    setCopied(true);
    toast.success("Número copiado");
    setTimeout(() => setCopied(false), 1800);
  };

  if (!info) return null;
  const provisioned = info.is_provisioned;

  return (
    <div
      data-testid="dashboard-call-from-detention"
      className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-blue-700 flex items-center justify-center">
          <Phone className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-black text-blue-900">
            Cómo llamar desde un centro de detención
          </p>
          <p className="text-xs text-blue-700">PBNDS 2011 · Sección 6.3</p>
        </div>
      </div>

      {provisioned ? (
        <div
          data-testid="dashboard-twilio-number"
          className="bg-white border-2 border-blue-300 rounded-xl p-4 flex items-center justify-between gap-3 mb-4"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
              Número de miapoyo
            </p>
            <p className="text-2xl sm:text-3xl font-black tracking-tight text-blue-900 font-mono">
              {info.phone_number}
            </p>
          </div>
          <button
            data-testid="dashboard-copy-number"
            onClick={copyNumber}
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-100 hover:bg-blue-200 flex items-center justify-center"
            aria-label="Copiar número"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-blue-800" />}
          </button>
        </div>
      ) : (
        <div
          data-testid="dashboard-twilio-pending"
          className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 mb-4 flex items-center gap-3"
        >
          <Sparkles className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <p className="text-sm text-amber-900 leading-snug">
            <span className="font-bold">Número pendiente.</span> Estamos terminando los últimos detalles técnicos. Te avisaremos en cuanto esté activo.
          </p>
        </div>
      )}

      <ol className="space-y-3 mb-1">
        <Step
          n={1}
          Icon={ListChecks}
          title="Añade el número a la PAN List"
          desc="Pide al detenido (o a un familiar afuera) que registre este número en la lista de contactos autorizados del centro. Los centros usan Securus, GTL o Talton, y solo permiten llamadas a números pre-aprobados. Asegúrate de que la cuenta tenga saldo."
        />
        <Step
          n={2}
          Icon={BookOpen}
          title="Pide el manual físico"
          desc="Ve a la biblioteca jurídica (Law Library) y solicita una copia impresa del Formulario I-589 y los manuales del Proyecto Florence o NIJC. El centro está obligado por ley federal a tenerlos. Lleva bolígrafo."
        />
        <Step
          n={3}
          Icon={Phone}
          title="Marca el número"
          desc="Desde los teléfonos públicos del penal, llama al número de miapoyo. La voz automatizada te dará la bienvenida y te pedirá tu código PIN."
        />
        <Step
          n={4}
          Icon={KeyRound}
          title="Da tu PIN dígito por dígito"
          desc="La IA te pedirá un dígito a la vez: 'dime el primer número', 'dime el segundo', y así. Esto evita errores por ruido. Tu PIN está arriba en esta pantalla."
        />
        <Step
          n={5}
          Icon={Sparkles}
          title="Sigue las instrucciones"
          desc="La IA te leerá los avisos legales, te preguntará qué formulario necesitas y te dictará casilla por casilla qué escribir en inglés. Tú hablas español, ella te ayuda a escribir en inglés deletreando cada palabra."
        />
      </ol>
    </div>
  );
}

function Step({ n, Icon, title, desc }) {
  return (
    <li className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center font-black text-sm">
        {n}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Icon className="w-4 h-4 text-blue-800" strokeWidth={2.5} />
          <p className="text-sm font-bold text-zinc-900">{title}</p>
        </div>
        <p className="text-xs text-zinc-700 leading-snug">{desc}</p>
      </div>
    </li>
  );
}
